import { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, RefreshCw, UserPlus, Sparkles } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';

import { ChainlitContext } from 'client-types/*';

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
}

const initialForm: SignupFormData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone: ''
};

export default function Signup() {
  const { t } = useTranslation();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<SignupFormData>(initialForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // ─── Validation ────────────────────────────────────────────────
  const passwordStrength = (pwd: string): { score: number; label: string; color: string } => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const labels = ['', t('signup.strength.weak'), t('signup.strength.medium'), t('signup.strength.good'), t('signup.strength.strong')];
    const colors = ['', 'bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];
    return { score, label: labels[score] || '', color: colors[score] || '' };
  };

  const strength = passwordStrength(formData.password);
  const passwordMismatch = formData.confirmPassword !== '' && formData.password !== formData.confirmPassword;

  const generatePassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let pwd = '';
    for (let i = 0; i < 16; i++) pwd += charset.charAt(Math.floor(Math.random() * charset.length));
    setFormData((f) => ({ ...f, password: pwd, confirmPassword: pwd }));
    setShowPassword(true);
  };

  const set = (field: keyof SignupFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((f) => ({ ...f, [field]: e.target.value }));

  // ─── Submit ────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('signup.errors.password_mismatch'));
      return;
    }
    if (strength.score < 2) {
      setError(t('signup.errors.password_too_weak'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(apiClient.buildEndpoint('/api/auth/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || t('signup.errors.generic'));
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Render : Success ─────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-950 dark:to-blue-950 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-10 max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('signup.success_title')}</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              <Trans
                i18nKey="signup.success_message"
                values={{ name: formData.firstName }}
                components={{ strong: <strong /> }}
              />
            </p>
          </div>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white rounded-xl py-5 font-bold"
          >
            {t('signup.sign_in')}
          </Button>
        </div>
      </div>
    );
  }

  // ─── Render : Form ────────────────────────────────────────────
  return (
    <div className="min-h-svh flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-950 dark:to-blue-950 p-4 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-cyan-500 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
            <Logo className="w-12 h-12" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">LegalChat</h1>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">by SmartLawyer</p>
                <span className="inline-flex items-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                  Beta
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-3">
              <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('signup.title')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('signup.subtitle')}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Prénom + Nom */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('signup.first_name')}</label>
                <input
                  type="text"
                  required
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={set('firstName')}
                  placeholder={t('signup.first_name_placeholder')}
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('signup.last_name')}</label>
                <input
                  type="text"
                  required
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={set('lastName')}
                  placeholder={t('signup.last_name_placeholder')}
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('signup.email')}</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={formData.email}
                onChange={set('email')}
                placeholder={t('signup.email_placeholder')}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
              />
            </div>

            {/* Téléphone */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('signup.phone')} <span className="text-muted-foreground font-normal">{t('signup.optional')}</span>
              </label>
              <input
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={set('phone')}
                placeholder={t('signup.phone_placeholder')}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
              />
            </div>

            {/* Mot de passe */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('signup.password')}</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={set('password')}
                    placeholder={t('signup.password_placeholder')}
                    className="flex h-10 w-full rounded-lg border border-input bg-background px-3 pr-10 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={generatePassword}
                  title={t('signup.generate_password')}
                  className="h-10 px-3 rounded-lg border border-input hover:bg-accent transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Force du mot de passe */}
              {formData.password && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          i <= strength.score ? strength.color : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{strength.label}</p>
                </div>
              )}
            </div>

            {/* Confirmer mot de passe */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('signup.confirm_password')}</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={set('confirmPassword')}
                  placeholder={t('signup.confirm_password_placeholder')}
                  className={`flex h-10 w-full rounded-lg border px-3 pr-10 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground bg-background ${
                    passwordMismatch ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordMismatch && (
                <p className="text-xs text-destructive">{t('signup.errors.password_mismatch')}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || passwordMismatch}
              className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white rounded-xl py-5 font-bold shadow-lg hover:shadow-cyan-500/30 transition-all mt-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  {t('signup.submitting')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  {t('signup.submit')}
                </span>
              )}
            </Button>
          </form>

          {/* Link to login */}
          <p className="text-center text-sm text-muted-foreground">
            {t('signup.have_account')}{' '}
            <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
              {t('signup.sign_in')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="font-semibold text-gray-700 dark:text-gray-300">SmartLawyer</span>
            <span>•</span>
            <span>{t('signup.tagline')}</span>
          </p>
          <p className="text-xs text-gray-400">{t('signup.footer')}</p>
        </div>
      </div>
    </div>
  );
}